import React, { useEffect } from 'react';
import RatingStars from '../../../react-components/rating-stars';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import type { BodyScrollOptions } from 'body-scroll-lock';
import { createDataLayerString, isInDialog } from '../../../helpers/helpers';
import { ExtendedWindowType } from '../../engine';
import { formatPhoneNumber } from '../../../helpers/transform';
import { Result } from '@coveo/headless';
import { ResultLink } from '../../components/interactive-result';
import { ContractorType } from '../../../helpers/enum';

type ContractorCardProps = {
  index: number;
  id: string;
  imgSource: string;
  imgAlt: string;
  contractorId: string;
  contractorType: string;
  contractorDBA: string;
  contractorName: string;
  contractorRating: number;
  contractorReviews: number;
  city: string;
  stateAbbreviation: string;
  distance: string;
  certificationsLabel: string;
  certifications: string[];
  contractorDesignations: string[];
  moreCertificationsLabel: string;
  viewAllCertificationsLabel: string;
  certificationImages: {
    facetName: string;
    iconUrl: string;
    key: string;
  }[];
  quoteLabel: string;
  phone: string;
  link: string;
  countryCode: string;
  isLead: number;
  result: Result;
};

function ContractorCard({
  index,
  imgSource,
  imgAlt,
  contractorId,
  contractorType,
  contractorDBA,
  contractorName,
  contractorRating,
  contractorReviews,
  city,
  stateAbbreviation,
  distance,
  certificationsLabel,
  certifications,
  moreCertificationsLabel,
  viewAllCertificationsLabel,
  certificationImages,
  quoteLabel,
  phone,
  link,
  countryCode,
  isLead,
  result
}: ContractorCardProps) {
  const modal = document.querySelector<HTMLElement>('.certifications-modal');
  const modalCloseButton = modal?.querySelector<HTMLElement>(
    '.modal__close-button'
  );

  const handleClick = () => {
    const options = {
      reserveScrollBarGap: true,
      allowTouchMove: (el: HTMLElement) => {
        return el.classList.contains('modal');
      }
    } as BodyScrollOptions;

    (modal as HTMLDialogElement)?.showModal();
    disableBodyScroll(modal as HTMLElement, options);

    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event_attributes: {
        contractor_id: contractorId,
        contractor_name: contractorName,
        contractor_certificates_count: certifications?.length || 0,
        contractor_rating: contractorRating,
        contractor_reviews_count: contractorReviews,
        contractor_certificate_name: certifications?.[0] || null,
        contractor_type: contractorType
      },
      event: 'contractor_interaction',
      event_category: 'contractor interactions',
      event_action: 'view certifications and awards'
    });
  };

  const handleClose = () => {
    (modal as HTMLDialogElement)?.close();
    clearAllBodyScrollLocks();
  };

  const handleRequestAQuote = () => {
    const modalContractorInfo = {
      id: contractorId,
      imageAlt: imgAlt,
      image: imgSource,
      contractorDBA: contractorDBA,
      contractorName: contractorName,
      rating: contractorRating,
      numberOfReviews: contractorReviews,
      city: city,
      state: stateAbbreviation,
      distanceinmiles: parseFloat(distance),
      certificateName: certifications?.[0] || null,
      numberOfCertificates: certifications?.length || 0,
      type: contractorType
    };

    const openRequestAQuoteModal = new CustomEvent('openRequestAQuoteModal', {
      detail: modalContractorInfo
    });

    (window as ExtendedWindowType).dataLayer.push({
      event_attributes: {
        contractor_id: modalContractorInfo.id,
        contractor_name: modalContractorInfo.contractorName,
        contractor_certificate_name: modalContractorInfo.certificateName,
        contractor_type: modalContractorInfo.type,
        contractor_reviews_count: modalContractorInfo.numberOfReviews,
        contractor_rating: modalContractorInfo.rating,
        contractor_certificates_count: modalContractorInfo.numberOfCertificates
      },
      event: 'request_quote_button',
      event_category: 'find a contractor',
      event_action: 'request a quote button',
      event_label: ''
    });
    window.dispatchEvent(openRequestAQuoteModal);
  };

  useEffect(() => {
    if (modalCloseButton) {
      modalCloseButton.addEventListener('click', handleClose);
    }

    modal?.addEventListener('click', (e: MouseEvent) => {
      if (e.target === modal && !isInDialog(modal as HTMLDialogElement, e)) {
        handleClose();
      }
    });

    const global = window as ExtendedWindowType;

    global.dataLayer?.push({
      event: 'contractor_impression',
      event_category: 'find a contractor',
      event_action: 'contractor impression',
      event_attributes: {
        contractor_id: contractorId,
        contractor_name: contractorName,
        contractor_certificate_name: certifications?.[0] || null,
        contractor_type: contractorType,
        contractor_reviews_count: contractorReviews,
        contractor_rating: contractorRating,
        contractor_certificates_count: certifications?.length || 0,
        contractor_position: index
      }
    });

    return () => {
      if (modalCloseButton) {
        modalCloseButton.removeEventListener('click', handleClose);
      }
    };
  }, []);

  const renderCertifications = () => {
    return (
      <div className="certification-card__certifications">
        <div className="certification-card__certifications-heading-wrapper">
          <h3 className="certification-card__certifications-heading">
            {certificationsLabel}
          </h3>
          <button
            type="button"
            className="certification-card__certifications-tooltip btn--tooltip btn--tooltip-alt"
            title={viewAllCertificationsLabel}
            onClick={handleClick}
            data-layer={createDataLayerString([
              {
                event: 'modal_view',
                event_category: 'modal overlay',
                event_action: 'view',
                event_attributes: {
                  modalName: 'certifications'
                }
              }
            ])}
          >
            <span className="sr-only">{viewAllCertificationsLabel}</span>
          </button>
        </div>
        {certifications && certifications.length > 0 && (
          <ul className="certification-card__certifications-list">
            {certifications.map((certification, index) => (
              <React.Fragment key={index}>
                {index < 3 && (
                  <li className="certification-card__certification">
                    <img
                      src={
                        certificationImages?.find(
                          item => item.facetName === certification
                        )?.iconUrl
                      }
                      alt=""
                      role="presentation"
                      className="certification-card__certification-icon"
                      width="20"
                      height="20"
                      loading="lazy"
                    />
                    {certification}
                  </li>
                )}
              </React.Fragment>
            ))}
            {certifications.length > 3 && (
              <li className="certification-card__certification">
                {`(+${certifications.length - 3} ${moreCertificationsLabel})`}
              </li>
            )}
          </ul>
        )}
      </div>
    );
  };

  const renderRequestAQuoteButton = () => {
    if (countryCode === 'CAN') return null;

    if (
      contractorType !== 'Residential' ||
      (contractorType === 'Residential' && isLead)
    ) {
      return (
        <button
          className="btn btn--secondary certification-card__quote"
          onClick={handleRequestAQuote}
        >
          {quoteLabel}
        </button>
      );
    } else {
      return null;
    }
  };

  return (
    <article className="certification-card">
      <ResultLink
        result={result}
        link={link}
        ariaLabel={`Go to ${contractorName}`}
        className="certification-card__image-link certification-card__image-link--desktop"
        dataLayer={createDataLayerString([
          {
            event: 'view_profile_click',
            event_category: 'contractor interactions',
            event_action: 'view profile click',
            event_label: '',
            event_attributes: {
              contractor_id: contractorId,
              contractor_name: contractorName,
              contractor_certificates_count: certifications?.length || 0,
              contractor_rating: contractorRating,
              contractor_reviews_count: contractorReviews,
              contractor_certificate_name: certifications?.[0] || null,
              contractor_type: contractorType
            }
          }
        ])}
      >
        <picture className="picture certification-card__picture">
          <img
            src={imgSource}
            alt={imgAlt}
            width={120}
            height={120}
            loading="lazy"
          />
        </picture>
      </ResultLink>
      <div className="certification-card__wrapper">
        <div className="certification-card__content">
          <div>
            <h2 className="certification-card__heading">
              <ResultLink
                className="link--inline"
                target="_blank"
                link={link}
                result={result}
                dataLayer={createDataLayerString([
                  {
                    event: 'view_profile_click',
                    event_category: 'contractor interactions',
                    event_action: 'view profile click',
                    event_label: '',
                    event_attributes: {
                      contractor_id: contractorId,
                      contractor_name: contractorName,
                      contractor_certificates_count:
                        certifications?.length || 0,
                      contractor_rating: contractorRating,
                      contractor_reviews_count: contractorReviews,
                      contractor_certificate_name: certifications?.[0] || null,
                      contractor_type: contractorType
                    }
                  }
                ])}
              >
                <span>{contractorName}</span>
              </ResultLink>
            </h2>
            {contractorReviews && contractorRating ? (
              <RatingStars
                rating={contractorRating}
                total={contractorReviews}
              />
            ) : null}
            <p className="certification-card__city">
              {city}, {stateAbbreviation} - {Number(distance).toFixed(1)} mi
            </p>
          </div>
          <div>
            <ResultLink
              link={link}
              result={result}
              ariaLabel={`Go to ${contractorName}`}
              className="certification-card__image-link certification-card__image-link--mobile"
              dataLayer={createDataLayerString([
                {
                  event: 'view_profile_click',
                  event_category: 'find a contractor',
                  event_action: 'view profile click',
                  event_label: '',
                  event_attributes: {
                    contractor_id: contractorId,
                    contractor_name: contractorName,
                    contractor_certificates_count: certifications?.length || 0,
                    contractor_rating: contractorRating,
                    contractor_reviews_count: contractorReviews,
                    contractor_certificate_name: certifications?.[0] || null,
                    contractor_type: contractorType
                  }
                }
              ])}
            >
              <picture className="picture certification-card__picture">
                <img
                  src={imgSource}
                  alt={imgAlt}
                  width={80}
                  height={80}
                  loading="lazy"
                />
              </picture>
            </ResultLink>
            {contractorType !== ContractorType.Commercial && <div className="certification-card__contact certification-card__contact--desktop">
              {renderRequestAQuoteButton()}
              {phone && (
                <a
                  href={`tel:${phone}`}
                  className="link--inline link--icon-left certification-card__phone"
                  data-layer={createDataLayerString([
                    {
                      event_category: 'find a contractor',
                      event_action: 'call us click phone number',
                      event_label: '',
                      event: 'contractor_leads',
                      event_attributes: {
                        contractor_id: contractorId,
                        contractor_name: contractorName,
                        contractor_certificates_count:
                          certifications?.length || 0,
                        contractor_rating: contractorRating,
                        contractor_reviews_count: contractorReviews,
                        contractor_type: contractorType,
                        contractor_certificate_name: certifications?.[0] || null
                      }
                    }
                  ])}
                >
                  <span className="sr-only">Phone Number:</span>
                  {formatPhoneNumber(phone)}
                  <svg viewBox="0 0 13 16" role="presentation">
                    <use xlinkHref="#red-phone-icon"></use>
                  </svg>
                </a>
              )}
            </div>}
          </div>
        </div>
        {certifications && certifications.length > 0
          ? renderCertifications()
          : null}
        {contractorType !== ContractorType.Commercial && (  
        <div className="certification-card__contact certification-card__contact--mobile">
          <a
            href={`tel:${phone}`}
            className="link--inline link--icon-left certification-card__phone"
            data-layer={createDataLayerString([
              {
                event_category: 'find a contractor',
                event_action: 'call us click phone number',
                event_label: '',
                event: 'call_us_phone_number_click',
                event_attributes: {
                  contractor_id: contractorId,
                  contractor_name: contractorName,
                  contractor_certificates_count: certifications?.length || 0,
                  contractor_rating: contractorRating,
                  contractor_reviews_count: contractorReviews,
                  contractor_type: contractorType,
                  contractor_certificate_name: certifications?.[0] || null
                }
              }
            ])}
          >
            <span className="sr-only">Phone Number:</span>
            {formatPhoneNumber(phone)}
            <svg viewBox="0 0 13 16" role="presentation">
              <use xlinkHref="#red-phone-icon"></use>
            </svg>
          </a>
          {renderRequestAQuoteButton()}
        </div>
        )}
      </div>
    </article>
  );
}

export default ContractorCard;
